/**
 * Shared accumulator helpers for multi-hop executor result bookkeeping.
 */

import type { ExecutorRunAccumulators } from "./executorToolRuntime";

export interface StepResultLineAccumulators {
  stepResults: string[];
  stepResultIndexById: Map<string, number>;
}

/**
 * Writes or replaces the human-readable result line for a step while keeping
 * dedupe keyed by stable executor step id.
 */
export function setStepResultLine(
  accumulators: StepResultLineAccumulators,
  stepId: string,
  line: string,
): void {
  const existingIndex = accumulators.stepResultIndexById.get(stepId);
  if (
    existingIndex !== undefined &&
    existingIndex >= 0 &&
    existingIndex < accumulators.stepResults.length
  ) {
    accumulators.stepResults[existingIndex] = line;
    return;
  }
  accumulators.stepResultIndexById.set(stepId, accumulators.stepResults.length);
  accumulators.stepResults.push(line);
}

/** Clear state whose addresses and evidence belong to the previously pinned workbook. */
export function resetWorkbookScopedRunAccumulators(
  accumulators: ExecutorRunAccumulators,
): void {
  accumulators.lastModifications = null;
  accumulators.editedCells = [];
  accumulators.formulaWriteTargets = [];
  accumulators.fdsFormulaWriteTargets = [];
  accumulators.fdsFormulaWriteAttempts = {};
  accumulators.unavailableFdsFormulaTargets = [];
  accumulators.fdsDateAxisWriteTargets = [];
  accumulators.autofitSheetNames.clear();
  accumulators.autofitTargets = [];
  accumulators.columnSizingHints = [];
  accumulators.columnSizingCellEvidence = {};
  accumulators.explicitColumnWidthKeys = [];
  accumulators.fdsRefreshSheets.clear();
  accumulators.deferredRefreshAllRequested = false;
  accumulators.deferredRecalcMode = null;
  accumulators.citationCellProvenance = {};
  accumulators.sourceNoteAudits = [];
}
